import { RESULTS_VIEW_MODES } from "../../lib/basic-polls/results-view-model";
import { BASIC_POLL_VOTE_TYPES } from "../../lib/basic-polls/constants";
import { OptionNoteButton } from "./option-note-button";

// Standalone results presentation for BasicPollVotingCard. Presentation ONLY —
// the privacy-gated view model (resultsVM) and all tally logic live upstream in
// results-view-model.js / irv.js / multiple-choice.js and are untouched here.
// This component never decides WHO may see results; it only renders the
// `results` snapshot it is handed, and only when the card has already gated it.
//
// Three surfaces share this panel:
//  - FINAL_RESULTS   → the hero "Results" section (winner/tied chips, zero-vote
//    empty state, IRV narrative). Results are the primary content once closed.
//  - LIVE_RESULTS    → the secondary "Live results" panel shown ALONGSIDE the
//    voting form while a poll is OPEN. Voting is the primary task while open,
//    so the live tally deliberately drops the winner/tied chips and the
//    zero-vote empty state — it is a live distribution, not a verdict.
//  - CLOSED_RESULTS  → a poll no longer accepting votes (deadline lapsed,
//    cancelled parent, or persisted CLOSED) but not finalized. Renders the same
//    distribution UI as LIVE (no verdict chips) under a "Results so far" heading
//    that does not imply live-updating. Behaves like LIVE everywhere except text.

function deriveMcWinnerIds(rows) {
  const maxCount = rows.reduce((max, row) => Math.max(max, row.count || 0), 0);
  return maxCount > 0 ? rows.filter((row) => (row.count || 0) === maxCount).map((row) => row.key) : [];
}

function labelForOption(option, fallbackIndex) {
  return option?.label || `Option ${fallbackIndex + 1}`;
}

function WinnerChip() {
  return (
    <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:border-emerald-700/70 dark:bg-emerald-900/30 dark:text-emerald-200">
      Winner
    </span>
  );
}

function TiedChip() {
  return (
    <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
      Tied
    </span>
  );
}

function EliminatedChip() {
  return (
    <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-card-foreground">
      Eliminated
    </span>
  );
}

function barWidth(value, max) {
  if (!Number.isFinite(max) || max <= 0) return 0;
  const pct = (value / max) * 100;
  return Math.max(pct, value > 0 ? 6 : 0);
}

function joinNames(names) {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function ResultBar({ fill, widthPct }) {
  const fillClass =
    fill === "winner" ? "bg-brand-primary" : "bg-muted-foreground/40";
  return (
    <div className="h-1.5 rounded-full bg-muted" role="presentation">
      <div
        className={`h-1.5 rounded-full ${fillClass}`}
        style={{ width: `${Math.min(100, Math.max(0, widthPct))}%` }}
      />
    </div>
  );
}

function MultipleChoiceBody({
  results,
  mode,
  optionsById,
  pollId,
  pollTitle,
  onViewOptionNote,
}) {
  const rows = Array.isArray(results.rows) ? results.rows : [];
  const isFinal = mode === RESULTS_VIEW_MODES.FINAL_RESULTS;

  const voterCount = results.voterCount;
  const finiteVoterCount = Number.isFinite(voterCount);
  const totalVotes = rows.reduce((sum, row) => sum + (row.count || 0), 0);
  const noVotesCast = finiteVoterCount ? voterCount === 0 : totalVotes === 0;

  // Distinct empty state for a finalized poll with zero ballots. Skips all
  // winner/tie logic — an all-zero tally is "no one voted", not "everything
  // tied". Only honored for FINAL results; a live tally just shows empty bars.
  if (isFinal && noVotesCast) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="poll-empty-results">
        No votes were cast.
      </p>
    );
  }

  const winnerIds = Array.isArray(results.winnerIds) && results.winnerIds.length > 0
    ? results.winnerIds
    : deriveMcWinnerIds(rows);
  const winnerSet = new Set(winnerIds);
  const isTie = isFinal && winnerSet.size > 1;

  const tieNames = isTie
    ? rows
        .filter((row) => winnerSet.has(row.key))
        .map((row) => row.label || optionsById.get(row.key)?.label || row.key)
    : [];

  return (
    <div className="space-y-2">
      {isTie ? (
        <p className="text-sm font-semibold text-card-foreground">
          Tie: {joinNames(tieNames)}
        </p>
      ) : null}
      {rows.map((row, index) => {
        const optionForRow = optionsById.get(row.key) || null;
        const label = row.label || labelForOption(optionForRow, index);
        const hasNote = String(optionForRow?.note || "").trim().length > 0;
        const count = row.count || 0;
        const percentage = Number.isFinite(row.percentage) ? row.percentage : 0;
        const voterWord = count === 1 ? "voter" : "voters";
        const isWinner = isFinal && !isTie && winnerSet.has(row.key);
        const isTiedRow = isTie && winnerSet.has(row.key);
        const fill = isWinner || isTiedRow ? "winner" : "neutral";
        const widthPct = Number.isFinite(row.percentage)
          ? Math.max(row.percentage, count > 0 ? 6 : 0)
          : 0;
        return (
          <div key={`${pollId}-result-${row.key || index}`} className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="flex min-w-0 items-center gap-2">
                {isWinner ? <WinnerChip /> : null}
                {isTiedRow ? <TiedChip /> : null}
                <span
                  className={`min-w-0 truncate text-card-foreground ${
                    isWinner || isTiedRow ? "font-semibold" : "font-medium"
                  }`}
                  title={label}
                >
                  {label}
                </span>
                {hasNote ? (
                  <OptionNoteButton
                    label={label}
                    onClick={() => onViewOptionNote?.(pollTitle, optionForRow)}
                  />
                ) : null}
              </span>
              <span className="tabular-nums text-xs text-muted-foreground">
                selected by {count} {voterWord} ({percentage}%)
              </span>
            </div>
            <ResultBar fill={fill} widthPct={widthPct} />
          </div>
        );
      })}
    </div>
  );
}

function IrvRounds({ rounds, sortedOptions, pollId, pollTitle, tiedFinalistIds = [], onViewOptionNote }) {
  // Precompute the set of options eliminated BEFORE each round (pure — no render
  // side effects). An option "eliminated that round" gets a strikethrough + an
  // "Eliminated" chip; options already inactive from prior rounds are dimmed.
  // A terminal TIE puts every tied finalist in the final round's eliminatedIds;
  // those are NOT eliminations (they tied for the win), so never mark them.
  const tiedFinalists = new Set(tiedFinalistIds || []);
  const eliminatedBeforeByRound = [];
  const running = new Set();
  rounds.forEach((roundData) => {
    eliminatedBeforeByRound.push(new Set(running));
    (roundData.eliminatedIds || []).forEach((id) => running.add(id));
  });

  return (
    <div className="space-y-2">
      {rounds.map((roundData, index) => {
        const roundNo = roundData?.round || index + 1;
        const counts = (roundData && roundData.counts) || {};
        const eliminatedThis = new Set(roundData?.eliminatedIds || []);
        const eliminatedBefore = eliminatedBeforeByRound[index] || new Set();
        const activeOptions = sortedOptions.filter((option) => !eliminatedBefore.has(option.id));
        const maxCount = activeOptions.reduce((max, option) => Math.max(max, counts[option.id] || 0), 0);
        // Only render the majority line when the round carries a real ballot
        // denominator; never derive one from an option count (dimensionally wrong).
        const hasNonExhausted = Number.isFinite(roundData?.nonExhausted);
        const nonExhausted = hasNonExhausted ? roundData.nonExhausted : null;
        const majority = hasNonExhausted ? Math.floor(nonExhausted / 2) + 1 : null;
        const exhausted = Number.isFinite(roundData?.exhausted) ? roundData.exhausted : 0;
        return (
          <div
            key={`${pollId}-round-${roundNo}`}
            className="rounded-lg border border-border bg-card px-3 py-2"
          >
            <p className="text-xs font-semibold text-card-foreground">Round {roundNo}</p>
            <div className="mt-1.5 space-y-1.5">
              {sortedOptions.map((option) => {
                const count = counts[option.id] ?? 0;
                const isInactive = eliminatedBefore.has(option.id);
                // A tied finalist appears in the final round's eliminatedIds but
                // did NOT lose — never strike it through or chip it "Eliminated".
                const isEliminatedThis =
                  eliminatedThis.has(option.id) && !tiedFinalists.has(option.id);
                const isLeader = !isInactive && !isEliminatedThis && count > 0 && count === maxCount;
                const hasNote = String(option?.note || "").trim().length > 0;
                // Only PRIOR-round inactive options are de-emphasized; the option
                // eliminated THIS round stays full-opacity with its strikethrough
                // + "Eliminated" chip so the elimination reads clearly.
                const dim = isInactive;
                // Scale the bar to the round's BALLOT total (non-exhausted), not
                // the leader's count — otherwise a 2-of-4 leader reads as a full
                // 100% bar, misstating the IRV proportion. Fall back to maxCount
                // only when the round carries no ballot denominator.
                const widthPct = isInactive
                  ? 0
                  : barWidth(count, hasNonExhausted ? nonExhausted : maxCount);
                return (
                  <div
                    key={`${pollId}-round-${roundNo}-${option.id}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {isEliminatedThis ? <EliminatedChip /> : null}
                      <span
                        // Prior-round inactive rows are de-emphasized via the
                        // muted-foreground token (still AA on the card surface) —
                        // NOT opacity, which dropped card-foreground below 4.5:1.
                        className={`min-w-0 truncate text-sm ${dim ? "text-muted-foreground" : "text-card-foreground"} ${
                          isEliminatedThis ? "line-through font-medium" : isLeader ? "font-semibold" : "font-medium"
                        }`}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                      {hasNote ? (
                        <OptionNoteButton
                          label={option.label}
                          onClick={() => onViewOptionNote?.(pollTitle, option)}
                        />
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span
                        className="hidden h-1.5 w-20 rounded-full bg-muted sm:block"
                        role="presentation"
                      >
                        <span
                          className={`block h-1.5 rounded-full ${isLeader ? "bg-brand-primary" : "bg-muted-foreground/40"}`}
                          style={{ width: `${widthPct}%` }}
                        />
                      </span>
                      <span className={`w-6 text-right tabular-nums text-xs ${dim ? "text-muted-foreground" : "text-card-foreground"}`}>
                        {count}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
            {hasNonExhausted ? (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Majority: {majority} of {nonExhausted} · Exhausted: {exhausted}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function RankedChoiceBody({
  results,
  mode,
  optionsById,
  sortedOptions,
  pollId,
  pollTitle,
  onViewOptionNote,
}) {
  const rounds = Array.isArray(results.rounds) ? results.rounds : [];
  const isFinal = mode === RESULTS_VIEW_MODES.FINAL_RESULTS;

  const voterCount = results.voterCount;
  const noVotesCast = Number.isFinite(voterCount) ? voterCount === 0 : rounds.length === 0;

  // Zero-ballot finalized IRV: an empty IRV otherwise "resolves" as a tie among
  // every option, which is misleading. Surface the same empty state as MC.
  if (isFinal && noVotesCast) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="poll-empty-results">
        No votes were cast.
      </p>
    );
  }

  const winnerIds = Array.isArray(results.winnerIds) ? results.winnerIds : [];
  const tiedIds = Array.isArray(results.tiedIds) ? results.tiedIds : [];
  const hasTie = isFinal && tiedIds.length > 1;
  const hasWinner = isFinal && winnerIds.length > 0;

  const winnerLabel = (id) => optionsById.get(id)?.label || id;
  const tiedNames = tiedIds.map(winnerLabel);

  const summary = (() => {
    if (!isFinal) return null;
    if (hasTie) {
      return (
        <p className="text-sm font-semibold text-card-foreground">
          Tie: {joinNames(tiedNames)}
        </p>
      );
    }
    if (hasWinner) {
      return (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <WinnerChip />
          <span className="font-semibold text-card-foreground">{winnerLabel(winnerIds[0])}</span>
        </p>
      );
    }
    return <p className="text-sm text-muted-foreground">No winner determined.</p>;
  })();

  if (rounds.length === 0) {
    return <div className="space-y-2">{summary}</div>;
  }

  const defaultOpen =
    mode === RESULTS_VIEW_MODES.LIVE_RESULTS ||
    mode === RESULTS_VIEW_MODES.CLOSED_RESULTS ||
    rounds.length <= 2;

  return (
    <div className="space-y-2">
      {summary}
      <details open={defaultOpen ? true : undefined} className="group">
        <summary className="cursor-pointer select-none rounded text-xs font-semibold text-card-foreground marker:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
          Round-by-round breakdown
        </summary>
        <div className="mt-2">
          <IrvRounds
            rounds={rounds}
            sortedOptions={sortedOptions}
            pollId={pollId}
            pollTitle={pollTitle}
            tiedFinalistIds={tiedIds}
            onViewOptionNote={onViewOptionNote}
          />
        </div>
      </details>
    </div>
  );
}

export function PollResultsPanel({
  results,
  mode,
  optionsById,
  sortedOptions = [],
  pollId,
  pollTitle,
  onViewOptionNote,
}) {
  if (!results) return null;
  const isFinal = mode === RESULTS_VIEW_MODES.FINAL_RESULTS;

  const body =
    results.voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE ? (
      <RankedChoiceBody
        results={results}
        mode={mode}
        optionsById={optionsById}
        sortedOptions={sortedOptions}
        pollId={pollId}
        pollTitle={pollTitle}
        onViewOptionNote={onViewOptionNote}
      />
    ) : (
      <MultipleChoiceBody
        results={results}
        mode={mode}
        optionsById={optionsById}
        pollId={pollId}
        pollTitle={pollTitle}
        onViewOptionNote={onViewOptionNote}
      />
    );

  // FINAL results are the hero — a top-separated "Results" section that sits on
  // the card surface (no muted box to bury it). LIVE results stay secondary: a
  // bordered panel alongside the form. CLOSED results share LIVE's bordered box
  // and distribution UI but head the section "Results so far" (no live-updating
  // implication). All headings use text-card-foreground so they clear WCAG AA
  // on the card surface (the old muted-on-muted "Live results" heading failed
  // at 4.28:1).
  if (isFinal) {
    return (
      <section className="mt-3 border-t border-border pt-3" aria-label="Poll results">
        <h5 className="text-sm font-semibold text-card-foreground">Results</h5>
        <div className="mt-2">{body}</div>
      </section>
    );
  }

  const isClosed = mode === RESULTS_VIEW_MODES.CLOSED_RESULTS;

  return (
    <section
      className="mt-3 rounded-xl border border-border bg-card px-3 py-3"
      aria-label={isClosed ? "Poll results" : "Live poll results"}
    >
      <h5 className="text-xs font-semibold text-card-foreground">
        {isClosed ? "Results so far" : "Live results"}
      </h5>
      <div className="mt-2">{body}</div>
    </section>
  );
}
